import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import { demoGovernanceRepository } from "./demoGovernanceRepository";
import { remoteGovernanceRepository } from "./remoteGovernanceRepository";
import { env } from "../../shared/config/env";

const key = ["v4-governance"];
const repository = (env.VITE_AUTH_MODE === "supabase"
  ? remoteGovernanceRepository
  : demoGovernanceRepository) as unknown as typeof demoGovernanceRepository;

export function useGovernance() {
  return useQuery({ queryKey: key, queryFn: () => repository.getState() });
}

export function useGovernanceActions() {
  const { session } = useAuth();
  const client = useQueryClient();
  const actor = () => {
    if (!session) throw new Error("请先登录。");
    return session.profile;
  };
  const refresh = () => client.invalidateQueries({ queryKey: key });
  const refreshAll = () => client.invalidateQueries();
  const target = useMutation({
    mutationFn: (
      input: Parameters<typeof demoGovernanceRepository.createTarget>[1],
    ) => repository.createTarget(actor(), input),
    onSuccess: refresh,
  });
  const actual = useMutation({
    mutationFn: (input: { id: string; value: number }) =>
      repository.updateActual(actor(), input.id, input.value),
    onSuccess: refresh,
  });
  const announcement = useMutation({
    mutationFn: (
      input: Parameters<typeof demoGovernanceRepository.publishAnnouncement>[1],
    ) => repository.publishAnnouncement(actor(), input),
    onSuccess: refresh,
  });
  const exportAudit = useMutation({
    mutationFn: async (entity: string) =>
      Promise.resolve(repository.recordExport(actor(), entity)),
    onSuccess: refresh,
  });
  const notification = useMutation({
    mutationFn: (id: string) => repository.markNotificationRead(actor(), id),
    onSuccess: refresh,
  });
  const safeDelete = useMutation({
    mutationFn: (input: {
      entityType: string;
      targetId: string;
      reason: string;
    }) => repository.safeDelete(actor(), input),
    onSuccess: refreshAll,
  });
  return {
    createTarget: target.mutateAsync,
    updateActual: actual.mutateAsync,
    publishAnnouncement: announcement.mutateAsync,
    recordExport: exportAudit.mutateAsync,
    markNotificationRead: notification.mutateAsync,
    safeDelete: safeDelete.mutateAsync,
    pending:
      target.isPending ||
      actual.isPending ||
      announcement.isPending ||
      exportAudit.isPending ||
      notification.isPending ||
      safeDelete.isPending,
  };
}
